import React, { useState, useEffect } from 'react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { FileText, ExternalLink, RefreshCw, Search, Filter, BookOpen, Newspaper, GraduationCap, Calendar, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sourceConfig = {
  'PubMed': { 
    icon: BookOpen, 
    color: '#4338CA', 
    bg: 'rgba(67, 56, 202, 0.08)',
    label: 'Letteratura Scientifica'
  },
  'AIMO': { 
    icon: Newspaper, 
    color: '#D97706', 
    bg: 'rgba(217, 119, 6, 0.08)',
    label: 'Medici Oculisti'
  },
  'SISO - Linee Guida': { 
    icon: GraduationCap, 
    color: '#059669', 
    bg: 'rgba(5, 150, 105, 0.08)',
    label: 'Linee Guida'
  },
  'SISO': { 
    icon: GraduationCap, 
    color: '#059669', 
    bg: 'rgba(5, 150, 105, 0.08)',
    label: 'Linee Guida'
  }
};

export const News = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsVisible(true);
    loadNews();
  }, []);

  const loadNews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/news/scientific?limit=15`);
      setArticles(response.data.articles || []);
      setLastUpdate(response.data.last_update);
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/news/refresh`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
      await loadNews();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = searchQuery === '' || 
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (article.summary && article.summary.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesSource = filterSource === 'all' || 
      article.source.toLowerCase().includes(filterSource.toLowerCase());
    
    return matchesSearch && matchesSource;
  });

  const uniqueSources = [...new Set(articles.map(a => a.source))];

  const getSourceConfig = (source) => {
    return sourceConfig[source] || { 
      icon: FileText, 
      color: '#2A9FCB', 
      bg: 'rgba(42, 159, 203, 0.08)',
      label: source 
    };
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
      <Navbar />
      
      {/* Hero Section - Luxury Style */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232A9FCB' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
          <span 
            className={`inline-block px-6 py-2 rounded-full text-sm font-medium tracking-widest uppercase mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ background: 'rgba(42, 159, 203, 0.1)', color: '#2A9FCB', fontFamily: "'Manrope', sans-serif" }}
          >
            Aggiornamenti Scientifici
          </span>
          <h1 
            className={`text-4xl sm:text-5xl lg:text-6xl mb-6 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
          >
            Linee Guida Oftalmologiche
          </h1>
          <p 
            className={`text-lg sm:text-xl max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ fontFamily: "'Manrope', sans-serif", color: '#666666' }}
          >
            Le ultime novità dal mondo dell'oftalmologia: articoli scientifici, 
            linee guida e raccomandazioni dalle principali società italiane e internazionali.
          </p>
        </div>
      </section>

      {/* Sources Info - Luxury Cards */}
      <section className="py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {Object.entries(sourceConfig).slice(0, 3).map(([source, config]) => {
              const IconComponent = config.icon;
              return (
                <div 
                  key={source}
                  className="flex items-center gap-3 px-6 py-4 rounded-2xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-gray-100 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all duration-300"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: config.bg }}
                  >
                    <IconComponent className="w-5 h-5" style={{ color: config.color }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#1A1A1A', fontFamily: "'Manrope', sans-serif" }}>
                      {source.replace(' - Linee Guida', '')}
                    </p>
                    <p className="text-xs" style={{ color: '#666666' }}>{config.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Filters - Luxury Style */}
      <section className="py-6 px-4 bg-white sticky top-0 z-20 border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#9CA3AF' }} />
              <input
                type="text"
                placeholder="Cerca negli articoli..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 rounded-full border focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]/30 transition-all"
                style={{ 
                  borderColor: '#E5E7EB', 
                  fontFamily: "'Manrope', sans-serif",
                  background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)'
                }}
              />
            </div>
            
            {/* Filter and Refresh */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] border border-gray-100">
                <Filter className="w-4 h-4" style={{ color: '#666666' }} />
                <select
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  className="bg-transparent focus:outline-none text-sm"
                  style={{ fontFamily: "'Manrope', sans-serif", color: '#1A1A1A' }}
                >
                  <option value="all">Tutte le fonti</option>
                  {uniqueSources.map(source => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
              </div>
              
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-3 rounded-full bg-[#2A9FCB] text-white hover:bg-[#1C729E] disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-[#2A9FCB]/20 border-t-[#2A9FCB] rounded-full animate-spin mb-4"></div>
              <p style={{ color: '#666666', fontFamily: "'Manrope', sans-serif" }}>Caricamento articoli...</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: '#9CA3AF' }} />
              <p className="text-lg" style={{ color: '#666666', fontFamily: "'Manrope', sans-serif" }}>
                Nessun articolo trovato
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article, idx) => {
                const config = getSourceConfig(article.source);
                const IconComponent = config.icon;
                
                return (
                  <article 
                    key={idx}
                    className="group bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgba(42,159,203,0.12)] transition-all duration-500 border border-gray-100 hover:border-[#2A9FCB]/20 flex flex-col"
                  >
                    {/* Source Header */}
                    <div 
                      className="px-5 py-3 flex items-center justify-between"
                      style={{ background: config.bg }}
                    >
                      <div className="flex items-center gap-2">
                        <IconComponent className="w-4 h-4" style={{ color: config.color }} />
                        <span 
                          className="text-xs font-medium"
                          style={{ color: config.color, fontFamily: "'Manrope', sans-serif" }}
                        >
                          {article.source}
                        </span>
                      </div>
                      {article.date && (
                        <div className="flex items-center gap-1 text-xs" style={{ color: '#9CA3AF' }}>
                          <Calendar className="w-3 h-3" />
                          {article.date}
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 
                        className="text-lg font-medium mb-3 line-clamp-3 group-hover:text-[#2A9FCB] transition-colors"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
                      >
                        {article.title}
                      </h3>
                      
                      {article.summary && (
                        <p 
                          className="text-sm mb-4 line-clamp-3 flex-1"
                          style={{ color: '#666666', fontFamily: "'Manrope', sans-serif", lineHeight: 1.7 }}
                        >
                          {article.summary}
                        </p>
                      )}
                      
                      <a
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium mt-auto pt-4 border-t border-gray-100 group-hover:gap-3 transition-all"
                        style={{ color: '#2A9FCB', fontFamily: "'Manrope', sans-serif" }}
                      >
                        Leggi l'articolo
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          
          {/* Last Update */}
          {lastUpdate && (
            <p 
              className="text-center text-sm mt-10 pt-6 border-t border-gray-100"
              style={{ color: '#9CA3AF', fontFamily: "'Manrope', sans-serif" }}
            >
              Ultimo aggiornamento: {new Date(lastUpdate).toLocaleString('it-IT')}
            </p>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: '#2A9FCB' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 
            className="text-3xl sm:text-4xl text-white mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Hai Domande sui Tuoi Occhi?
          </h2>
          <p 
            className="text-white/80 text-lg mb-8 max-w-2xl mx-auto"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            I nostri specialisti sono a tua disposizione per rispondere a qualsiasi dubbio.
          </p>
          <a 
            href="/prenota" 
            className="inline-flex items-center justify-center bg-white text-[#2A9FCB] hover:bg-gray-100 rounded-full px-10 py-4 text-sm font-semibold transition-all duration-300 shadow-lg"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Prenota una Consulenza
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default News;
